import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../utils/api';

/**
 * MessageComposer
 * ----------------
 * Single shared dialog used by every "Message" entry point in the app.
 *
 * Audience modes (chosen by the sender, role-gated):
 *   • "one"          — one specific recipient. The recipient picker
 *                       supports either a parent (about a specific
 *                       child, optional for admin/staff) OR another
 *                       staff/admin colleague (no child needed).
 *   • "all_staff"    — broadcast to every active staff/admin.
 *                       Available to staff + admin/org_owner/org_admin.
 *   • "all_parents"  — broadcast to every active parent.
 *                       Admin / org_owner / org_admin ONLY.
 *
 * Parent role: locked to "one" with `child_id` mandatory (rule c).
 *
 * On send the dialog calls `POST /api/communications`. The backend
 * (server.py) handles the broadcast fan-out and per-recipient push.
 */

const MESSAGE_TYPES = [
  { value: 'general', label: 'General' },
  { value: 'behavior', label: 'Behaviour' },
  { value: 'learning', label: 'Learning' },
  { value: 'health', label: 'Health' },
  { value: 'pickup_dropoff', label: 'Pickup / Dropoff' },
  { value: 'achievement', label: 'Achievement' },
];

export const MessageComposer = ({
  open,
  onClose,
  onSent,
  user,                  // current_user passed by the dashboard
  parentUserId = null,
  childId = null,
  childName = null,
  parentName = null,
}) => {
  const role = user?.role;
  const isParent = role === 'parent';
  const ADMIN_ROLES = ['admin', 'org_owner', 'org_admin'];
  const isAdmin = ADMIN_ROLES.includes(role);
  const isStaff = role === 'staff';
  const canBroadcastAllStaff = isStaff || isAdmin;
  const canBroadcastAllParents = isAdmin;

  // Audience state
  const [audience, setAudience] = useState('one'); // one | all_staff | all_parents
  // For "one" mode: who's the recipient — a Parent (optionally about a
  // child) or a Staff colleague.
  const [recipientGroup, setRecipientGroup] = useState('parent'); // parent | staff

  const [resolvedRecipientId, setResolvedRecipientId] = useState(parentUserId || '');
  const [resolvedRecipientLabel, setResolvedRecipientLabel] = useState(parentName || '');
  const [selectedChildId, setSelectedChildId] = useState(childId || '');
  const [selectedChildName, setSelectedChildName] = useState(childName || '');

  const [parentChoices, setParentChoices] = useState([]);
  const [staffChoices, setStaffChoices] = useState([]);
  const [childChoices, setChildChoices] = useState([]);

  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('general');
  const [priority, setPriority] = useState('normal');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // When the dialog opens, fill any context we can.
  useEffect(() => {
    if (!open) return;
    setError('');
    setSubmitting(false);
    setSubject('');
    setMessage('');
    setMessageType('general');
    setPriority('normal');
    setAudience('one');
    setRecipientGroup(isParent ? 'parent' : (parentUserId || childId ? 'parent' : 'parent'));
    setResolvedRecipientId(parentUserId || '');
    setResolvedRecipientLabel(parentName || '');
    setSelectedChildId(childId || '');
    setSelectedChildName(childName || '');

    let cancelled = false;

    (async () => {
      try {
        if (isParent) {
          // Parent path — needs a child. If we don't have one, fetch theirs.
          if (!childId) {
            const kids = await api.getChildren().catch(() => []);
            if (cancelled) return;
            const active = (kids || []).filter(k => !k.is_archived);
            setChildChoices(active);
            if (active.length === 1) {
              setSelectedChildId(active[0]._id);
              setSelectedChildName(`${active[0].first_name} ${active[0].last_name}`.trim());
            }
          }
          return;
        }

        // Admin / staff: load both parent + staff lists for the picker
        // and resolve childId → parent if a child context was passed in.
        try {
          const users = await api.getDirectoryUsers();
          if (cancelled) return;
          const safeUsers = Array.isArray(users) ? users : [];
          setParentChoices(safeUsers.filter(
            u => u.role === 'parent'
              && u.approval_status !== 'pending'
              && u.approval_status !== 'rejected'
              && u.is_active !== false
          ));
          setStaffChoices(safeUsers.filter(
            u => ['staff', 'admin', 'org_owner', 'org_admin'].includes(u.role)
              && u._id !== user?._id
              && u.is_active !== false
          ));
        } catch (e) {
          if (!cancelled) setError(e.message);
        }

        // Resolve parent from childId if needed
        if (childId && !parentUserId) {
          try {
            const c = await api.getChildren();
            const found = (c || []).find(x => x._id === childId);
            if (found && !cancelled) {
              setSelectedChildName(`${found.first_name} ${found.last_name}`.trim());
              if (found.parent_id) setResolvedRecipientId(found.parent_id);
            }
          } catch { /* ignore */ }
        }
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      }
    })();

    return () => { cancelled = true; };
  }, [open, isParent, parentUserId, childId, parentName, childName, user?._id]);

  // When the user toggles audience or recipient group, clear the
  // single-recipient state so we don't accidentally send to a stale id.
  useEffect(() => {
    if (audience !== 'one') {
      setResolvedRecipientId('');
      setResolvedRecipientLabel('');
      setSelectedChildId('');
      setSelectedChildName('');
    }
  }, [audience]);
  useEffect(() => {
    if (audience !== 'one') return;
    setResolvedRecipientId('');
    setResolvedRecipientLabel('');
    if (recipientGroup === 'staff') {
      setSelectedChildId('');
      setSelectedChildName('');
    }
  }, [recipientGroup, audience]);

  const recipientHint = useMemo(() => {
    if (isParent) return 'This message will be sent to your childcare provider staff.';
    if (audience === 'all_staff') return '📣 Broadcasting to ALL staff members.';
    if (audience === 'all_parents') return '📣 Broadcasting to ALL parents in the organisation.';
    if (resolvedRecipientLabel) {
      return selectedChildName
        ? `To ${resolvedRecipientLabel} (about ${selectedChildName}).`
        : `To ${resolvedRecipientLabel}.`;
    }
    if (resolvedRecipientId) {
      return selectedChildName ? `About ${selectedChildName}.` : 'Direct message.';
    }
    return null;
  }, [isParent, audience, resolvedRecipientLabel, resolvedRecipientId, selectedChildName]);

  if (!open) return null;

  const submit = async (e) => {
    e?.preventDefault?.();
    setError('');
    if (!subject.trim() || !message.trim()) {
      setError('Subject and message are required.');
      return;
    }

    if (audience === 'one') {
      if (isParent && !selectedChildId) {
        setError('Please pick which child this is about.');
        return;
      }
      if (!isParent && !resolvedRecipientId) {
        setError(`Pick a ${recipientGroup === 'staff' ? 'staff member' : 'parent'} to send to.`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const payload = {
        subject: subject.trim(),
        message: message.trim(),
        message_type: messageType,
        priority,
      };

      if (audience === 'one') {
        if (selectedChildId) payload.child_id = selectedChildId;
        if (resolvedRecipientId) payload.sent_to_user_id = resolvedRecipientId;
      } else {
        payload.broadcast_target = audience;
      }

      const created = await api.sendCommunication(payload);
      if (onSent) onSent(created);
      onClose?.();
    } catch (e) {
      setError(e.message || 'Failed to send');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={() => !submitting && onClose?.()} data-testid="message-composer-overlay">
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 600 }} data-testid="message-composer">
        <div className="modal-header">
          <h2 style={{ margin: 0 }}>✉️ New message</h2>
        </div>
        <form onSubmit={submit} autoComplete="off">
          {/* Audience picker (non-parents only) */}
          {!isParent && !parentUserId && !childId && (
            <div className="form-group">
              <label>Send to</label>
              <div role="radiogroup" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }} data-testid="composer-audience">
                <button
                  type="button"
                  className={`btn ${audience === 'one' ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => setAudience('one')}
                  data-testid="audience-one"
                >👤 One person</button>
                {canBroadcastAllStaff && (
                  <button
                    type="button"
                    className={`btn ${audience === 'all_staff' ? 'btn-primary' : 'btn-ghost'}`}
                    onClick={() => setAudience('all_staff')}
                    data-testid="audience-all-staff"
                  >📣 All staff</button>
                )}
                {canBroadcastAllParents && (
                  <button
                    type="button"
                    className={`btn ${audience === 'all_parents' ? 'btn-primary' : 'btn-ghost'}`}
                    onClick={() => setAudience('all_parents')}
                    data-testid="audience-all-parents"
                  >📣 All parents</button>
                )}
              </div>
            </div>
          )}

          {/* For "one person" — pick parent vs staff colleague */}
          {!isParent && audience === 'one' && !parentUserId && !childId && (
            <div className="form-group">
              <label>Recipient type</label>
              <div role="radiogroup" style={{ display: 'flex', gap: '0.5rem' }} data-testid="composer-recipient-group">
                <button
                  type="button"
                  className={`btn ${recipientGroup === 'parent' ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => setRecipientGroup('parent')}
                  data-testid="recipient-group-parent"
                >👨‍👩‍👧 Parent</button>
                <button
                  type="button"
                  className={`btn ${recipientGroup === 'staff' ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => setRecipientGroup('staff')}
                  data-testid="recipient-group-staff"
                >👥 Staff colleague</button>
              </div>
            </div>
          )}

          {recipientHint && (
            <div style={{
              marginBottom: '0.75rem',
              padding: '0.5rem 0.75rem',
              background: 'hsl(var(--muted) / 0.5)',
              borderRadius: 6,
              fontSize: '0.85rem',
            }} data-testid="composer-recipient-hint">
              {recipientHint}
            </div>
          )}

          {/* Single recipient picker */}
          {!isParent && audience === 'one' && !parentUserId && !childId && recipientGroup === 'parent' && parentChoices.length > 0 && (
            <div className="form-group">
              <label>To parent</label>
              <select
                value={resolvedRecipientId}
                onChange={(e) => {
                  const id = e.target.value;
                  setResolvedRecipientId(id);
                  const p = parentChoices.find(x => x._id === id);
                  setResolvedRecipientLabel(p ? (p.full_name || p.email) : '');
                }}
                required
                data-testid="composer-parent-select"
              >
                <option value="">— Select parent —</option>
                {parentChoices.map(p => (
                  <option key={p._id} value={p._id}>{p.full_name || '—'} · {p.email}</option>
                ))}
              </select>
            </div>
          )}

          {!isParent && audience === 'one' && !parentUserId && !childId && recipientGroup === 'staff' && (
            <div className="form-group">
              <label>To colleague</label>
              <select
                value={resolvedRecipientId}
                onChange={(e) => {
                  const id = e.target.value;
                  setResolvedRecipientId(id);
                  const s = staffChoices.find(x => x._id === id);
                  setResolvedRecipientLabel(s ? (s.full_name || s.email) : '');
                }}
                required
                data-testid="composer-staff-select"
              >
                <option value="">— Select colleague —</option>
                {staffChoices.map(s => (
                  <option key={s._id} value={s._id}>
                    {s.full_name || '—'} · {s.email}
                    {s.role && s.role !== 'staff' ? ` · ${s.role}` : ''}
                  </option>
                ))}
              </select>
              {staffChoices.length === 0 && (
                <small style={{ color: 'hsl(var(--muted-foreground))' }}>No other staff members available.</small>
              )}
            </div>
          )}

          {/* Parent: pick child */}
          {isParent && !childId && childChoices.length > 1 && (
            <div className="form-group">
              <label>About which child?</label>
              <select
                value={selectedChildId}
                onChange={(e) => {
                  const id = e.target.value;
                  setSelectedChildId(id);
                  const c = childChoices.find(x => x._id === id);
                  setSelectedChildName(c ? `${c.first_name} ${c.last_name}`.trim() : '');
                }}
                required
                data-testid="composer-child-select"
              >
                <option value="">— Select child —</option>
                {childChoices.map(c => (
                  <option key={c._id} value={c._id}>{c.first_name} {c.last_name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="form-group">
            <label>Subject</label>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
              data-testid="composer-subject"
              maxLength={120}
            />
          </div>

          <div className="form-group">
            <label>Category</label>
            <select value={messageType} onChange={(e) => setMessageType(e.target.value)} data-testid="composer-type">
              {MESSAGE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>

          <div className="form-group">
            <label>Priority</label>
            <select value={priority} onChange={(e) => setPriority(e.target.value)} data-testid="composer-priority">
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>

          <div className="form-group">
            <label>Message</label>
            <textarea
              rows={5}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
              data-testid="composer-body"
            />
          </div>

          {error && (
            <div style={{ color: 'hsl(0 70% 35%)', marginBottom: 8 }} data-testid="composer-error">
              {error}
            </div>
          )}

          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" disabled={submitting}
              onClick={() => onClose?.()} data-testid="composer-cancel">Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting} data-testid="composer-send">
              {submitting ? 'Sending…' : (audience === 'one' ? 'Send message' : 'Send broadcast')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MessageComposer;
